import { LongGameError } from '@long-game/common';
import { BaseSdk } from './BaseSdk.js';
export class PublicSdk extends BaseSdk {
    getMe = this.sdkQuery('getMe', this.apiRpc.users.me.$get, {
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    });
    updateMe = this.sdkMutation(this.apiRpc.users.me.$put, {
        transformInput: (input) => ({ json: input }),
        invalidate: [['getMe']],
    });
    setAvatar = this.sdkMutation(this.apiRpc.users.me.avatar.$put, {
        transformInput: (input) => {
            return { form: { file: input.image } };
        },
        invalidate: [['getMe']],
    });
    getUser = this.sdkQuery('getUser', this.apiRpc.users[':id'].$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    });
    getFriendships = this.sdkQuery('getFriendships', this.apiRpc.friendships.$get, {
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    });
    getFriendshipInvites = this.sdkQuery('getFriendshipInvites', this.apiRpc.friendships.invites.$get, {
        transformInput: (input) => ({
            query: { direction: input.direction },
        }),
    });
    sendFriendshipInvite = this.sdkMutation(this.apiRpc.friendships.invites.$post, {
        transformInput: (input) => ({ json: input }),
        invalidate: [
            ['getFriendshipInvites'],
            ['getFriendships'],
            ['getGameSessionPregame'],
        ],
    });
    respondToFriendshipInvite = this.sdkMutation(this.apiRpc.friendships.invites[':id'].$post, {
        transformInput: (input) => ({
            json: { response: input.response },
            param: { id: input.id },
        }),
        invalidate: [
            ['getPublicFriendInvite'],
            ['getFriendshipInvites'],
            ['getFriendships'],
        ],
    });
    getPublicFriendshipInvite = this.sdkQuery('getPublicFriendshipInvite', this.apiRpc.friendships.invites[':id'].$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
    });
    getGameSessions = this.sdkInfiniteQuery('getGameSessions', ({ status, invitationStatus }, cursor) => this.apiRpc.gameSessions.$get({
        query: { before: cursor, status, invitationStatus },
    }), {
        transformInput: (input) => input,
        getKey: (input) => {
            const key = [];
            if (input.invitationStatus) {
                key.push(input.invitationStatus);
            }
            if (input.status) {
                key.push(...input.status.sort());
            }
            return key;
        },
    });
    getRemainingGameSessions = this.sdkQuery('getRemainingGameSessions', this.apiRpc.gameSessions.remaining.$get);
    getGameSessionInvitations = this.sdkQuery('getGameSessionInvitations', this.apiRpc.gameSessionInvitations.$get);
    respondToGameSessionInvitation = this.sdkMutation(this.apiRpc.gameSessionInvitations[':id'].$put, {
        transformInput: (input) => ({
            json: { response: input.response },
            param: { id: input.id },
        }),
        invalidate: [['getGameSessionInvitations'], ['getGameSessionPregame']],
    });
    sendGameSessionInvitation = this.sdkMutation(this.apiRpc.gameSessionInvitations.$post, {
        transformInput: (input) => ({
            json: input,
        }),
        invalidate: [['getGameSessionInvitations'], ['getGameSessionPregame']],
    });
    cancelGameSessionInvitation = this.sdkMutation(this.apiRpc.gameSessionInvitations[':id'].$delete, {
        transformInput: (input) => ({
            param: { id: input.id },
        }),
        invalidate: [['getGameSessionInvitations'], ['getGameSessionPregame']],
    });
    getPublicGameSessionFromInviteCode = this.sdkQuery('getPublicGameSessionFromInviteCode', this.apiRpc.public.publicInviteGameInfo[':code'].$get, {
        transformInput: (input) => ({ param: { code: input } }),
    });
    claimPublicGameSessionLink = this.sdkMutation(this.apiRpc.gameSessionInvitations.claimCode[':code'].$post, {
        transformInput: (input) => ({
            param: { code: input.code },
        }),
        invalidate: [['getGameSessionInvitations'], ['getGameSessionPregame']],
    });
    getPublicGameSessionLink = this.sdkQuery('getPublicGameSessionLink', this.apiRpc.gameSessions[':id'].inviteLink.$get, {
        transformInput: (input) => ({
            param: { id: input.id },
        }),
    });
    getGames = this.sdkQuery('getGames', this.apiRpc.games.$get, {
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
        transformInput: (input) => ({
            query: {
                prerelease: input?.prerelease ? 'true' : 'false',
            },
        }),
    });
    getGame = this.sdkQuery('getGame', this.apiRpc.games[':id'].$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
        enabled(input) {
            return !!input.id;
        },
        ignoreError(err) {
            return err.code === LongGameError.Code.NotFound;
        },
        emptyResponse: {
            id: 'empty',
            creators: [],
            title: 'Unknown Game',
            description: '',
            prerelease: false,
            versions: [{ version: 'v1', url: '' }],
            screenshots: [],
            tags: [],
        },
    });
    getGameVersionDetails = this.sdkQuery('getGameVersionDetails', this.apiRpc.games[':id'][':version'].details.$get, {
        transformInput: (input) => ({
            param: { id: input.id ?? 'empty', version: input.version ?? 'v1' },
        }),
        enabled(input) {
            return !!input.id && !!input.version;
        },
    });
    getResolvedGameIdFromAlias = this.sdkQuery('getResolvedGameIdFromAlias', this.apiRpc.games.resolveAlias[':aliasId'].$get, {
        transformInput: (input) => ({
            param: { aliasId: input.aliasId },
        }),
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
        enabled(input) {
            return !!input.aliasId && input.aliasId !== 'empty';
        },
    });
    // yeah this is on the game session API, but it's
    // kind of more useful here.
    prepareGameSession = this.sdkMutation(this.apiRpc.gameSessions.$post, {
        transformInput: (input) => ({
            json: { gameId: input.gameId },
        }),
    });
    startGameSession = this.sdkMutation(this.apiRpc.gameSessions[':id'].start.$post, {
        transformInput: (input) => ({ param: { id: input.id } }),
        onSuccess: (output, vars) => {
            this.queryClient.setQueryData(['getGameSessionStatus', { id: vars.id }], output.status);
        },
    });
    updateGameSession = this.sdkMutation(this.apiRpc.gameSessions[':id'].$put, {
        transformInput: (input) => ({
            json: { gameId: input.gameId },
            param: { id: input.id },
        }),
        onSuccess: (output, vars) => {
            this.queryClient.setQueryData(['getGameSessionStatus', { id: vars.id }], output.session.status);
            this.queryClient.invalidateQueries({
                queryKey: ['getGameSessionDetails', { id: vars.id }],
            });
            this.queryClient.invalidateQueries({
                queryKey: ['getGameSessionPregame', { id: vars.id }],
            });
        },
    });
    deleteGameSession = this.sdkMutation(this.apiRpc.gameSessions[':id'].$delete, {
        transformInput: (input) => ({
            param: { id: input.id },
        }),
        invalidate: [
            ['getGameSessions'],
            ['getGameSessionStatus'],
            ['getGameSessionPregame'],
        ],
    });
    abandonGameSession = this.sdkMutation(this.apiRpc.gameSessions[':id'].abandon.$post, {
        transformInput: (input) => ({
            param: { id: input.id },
        }),
        invalidate: [
            ['getGameSessions'],
            ['getGameSessionStatus'],
            ['getGameSessionPregame'],
        ],
    });
    getGameSessionStatus = this.sdkQuery('getGameSessionStatus', this.apiRpc.gameSessions[':id'].status.$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
    });
    getGameSessionDetails = this.sdkQuery('getGameSessionDetails', this.apiRpc.gameSessions[':id'].$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
    });
    getGameSessionMembers = this.sdkQuery('getGameSessionMembers', this.apiRpc.gameSessions[':id'].members.$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
    });
    getGameSessionPregame = this.sdkQuery('getGameSessionPregame', this.apiRpc.gameSessions[':id'].pregame.$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
    });
    getGameSessionPlayerStatuses = this.sdkQuery('getGameSessionPlayerStatuses', this.apiRpc.gameSessions[':id'].playerStatuses.$get, {
        transformInput: (input) => ({
            param: { id: input.id },
        }),
        getKey: (input) => {
            return ['getGameSessionPlayerStatuses', input.id];
        },
        enabled: (input) => input.enabled !== false,
    });
    getGameSessionRound = this.sdkQuery('getGameSessionRound', this.apiRpc['gameSessions'][':id'].rounds[':index'].$get, {
        transformInput: (input) => ({
            param: { id: input.id, index: input.index.toString() },
        }),
    });
    getGameSessionPostgame = this.sdkQuery('getGameSessionPostgame', this.apiRpc.gameSessions[':id'].postgame.$get, {
        transformInput: (input) => ({ param: { id: input.id } }),
    });
    getAvailableGames = this.sdkQuery('getAvailableGames', this.apiRpc.gameSessions[':id'].availableGames.$get, {
        transformInput: (input) => ({
            param: { id: input.id },
        }),
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    });
    createPushSubscription = this.sdkMutation(this.apiRpc.push.$post, {
        transformInput: (input) => ({
            json: input,
        }),
    });
    deletePushSubscription = this.sdkMutation(this.apiRpc.push[':endpoint'].$delete, {
        transformInput: (input) => ({
            param: { endpoint: input.endpoint },
        }),
    });
    getNotifications = this.sdkInfiniteQuery('getNotifications', ({ status }, cursor) => this.apiRpc.notifications.$get({
        query: {
            after: cursor,
            status,
        },
    }), {
        transformInput: (input) => ({
            status: input.status,
        }),
        getKey: (input) => {
            const key = [];
            if (input.status) {
                key.push(input.status);
            }
            return key;
        },
        defaults: {
            refetchInterval: 1000 * 60 * 15,
        },
    });
    markNotificationAsRead = this.sdkMutation(this.apiRpc.notifications[':id'].$put, {
        transformInput: (input) => ({
            param: { id: input.id },
            json: { read: input.read },
        }),
        invalidate: [['getNotifications']],
    });
    deleteNotification = this.sdkMutation(this.apiRpc.notifications[':id'].$delete, {
        transformInput: (input) => ({
            param: { id: input.id },
        }),
        invalidate: [['getNotifications']],
    });
    getNotificationSettings = this.sdkQuery('getNotificationSettings', this.apiRpc.users.me.notificationSettings.$get, {
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    });
    updateNotificationSettings = this.sdkMutation(this.apiRpc.users.me.notificationSettings.$put, {
        transformInput: (input) => ({
            json: input,
        }),
        invalidate: [['getNotificationSettings']],
    });
    markAllNotificationsAsRead = this.sdkMutation(this.apiRpc.notifications.markAllRead.$post, {
        transformInput: () => ({}),
        invalidate: [['getNotifications']],
    });
    getOwnedGames = this.sdkQuery('getOwnedGames', this.apiRpc.games.owned.$get);
    applyFreeGames = this.sdkMutation(this.apiRpc.games.applyFree.$post, {
        invalidate: [['getOwnedGames']],
    });
    getGameProducts = this.sdkQuery('gameProducts', this.apiRpc.games.products.$get, {
        transformInput: (input) => ({
            query: input,
        }),
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    });
    getGameProduct = this.sdkQuery('gameProduct', this.apiRpc.games.products[':productId'].$get, {
        transformInput: (input) => ({
            param: { productId: input.id },
        }),
        enabled: (input) => !!input.id,
        defaults: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    });
    adminCreateGameProduct = this.sdkMutation(this.apiRpc.admin.gameProducts.$post, {
        invalidate: [['gameProducts']],
    });
    adminUpdateGameProduct = this.sdkMutation(this.apiRpc.admin.gameProducts[':productId'].$put, {
        transformInput: (input) => ({
            param: { productId: input.id },
            json: {
                name: input.name,
                priceCents: input.priceCents,
                description: input.description ?? undefined,
                publishedAt: input.publishedAt ?? undefined,
            },
        }),
        invalidate: [['gameProducts']],
    });
    adminDeleteGameProduct = this.sdkMutation(this.apiRpc.admin.gameProducts[':productId'].$delete, {
        transformInput: (input) => ({
            param: { productId: input.id },
        }),
        invalidate: [['gameProducts']],
    });
    adminAddGameProductItem = this.sdkMutation(this.apiRpc.admin.gameProducts[':productId'].items.$put, {
        transformInput: (input) => ({
            param: { productId: input.id },
            json: { gameId: input.gameId },
        }),
        invalidate: [['gameProducts']],
    });
    adminRemoveGameProductItem = this.sdkMutation(this.apiRpc.admin.gameProducts[':productId'].items[':itemId'].$delete, {
        transformInput: (input) => ({
            param: { productId: input.id, itemId: input.itemId },
            invalidate: [['gameProducts']],
        }),
    });
    adminGetAllGameSessions = this.sdkInfiniteQuery('adminGetAllGameSessions', (input, cursor) => this.apiRpc.admin.gameSessions.$get({
        query: {
            ...input,
            before: cursor,
        },
    }), {
        transformInput: (input) => ({
            query: { status: input.status },
        }),
        getKey: (input) => {
            const key = [];
            if (input.status) {
                key.push(input.status);
            }
            return key;
        },
    });
    adminDeleteGameSession = this.sdkMutation(this.apiRpc.admin.gameSessions[':sessionId'].$delete, {
        transformInput: (input) => ({
            param: { sessionId: input.id },
        }),
        invalidate: [['adminGetAllGameSessions']],
    });
    adminDumpGameSessionDb = this.sdkMutation(this.apiRpc.admin.gameSessions[':sessionId'].db.$get, {
        transformInput: (input) => ({
            param: { sessionId: input.id },
        }),
    });
    adminUpdateGameSessionTimezone = this.sdkMutation(this.apiRpc.admin.gameSessions[':sessionId'].timezone.$put, {
        transformInput: (input) => ({
            param: { sessionId: input.id },
            json: { timezone: input.timezone },
        }),
        invalidate: [['adminGetAllGameSessions']],
    });
    adminGetGameSessionDetails = this.sdkQuery('adminGetGameSessionDetails', this.apiRpc.admin.gameSessions[':sessionId'].details.$get, {
        transformInput: (input) => ({
            param: { sessionId: input.id },
        }),
        enabled: (input) => !!input.id,
    });
    adminGetUsers = this.sdkInfiniteQuery('adminGetUsers', (input, cursor) => this.apiRpc.admin.users.$get({
        query: {
            before: cursor,
            first: input.first ?? 20,
        },
    }), {
        transformInput: (input) => ({
            first: input.first,
        }),
        getKey: (input) => {
            const key = [];
            if (input.first) {
                key.push(input.first.toString());
            }
            return key;
        },
    });
    adminDeleteUser = this.sdkMutation(this.apiRpc.admin.users[':userId'].$delete, {
        transformInput: (input) => ({
            param: { userId: input.userId },
        }),
        invalidate: [['adminGetUsers']],
    });
    adminSendTestNotification = this.sdkMutation(this.apiRpc.admin.notifications['send-test'][':userId'].$post, {
        transformInput: (input) => ({
            param: { userId: input.userId },
        }),
    });
    adminSetGameSessionLeader = this.sdkMutation(this.apiRpc.admin.gameSessions[':sessionId'].leader.$put, {
        transformInput: (input) => ({
            param: { sessionId: input.sessionId },
            json: { leaderId: input.leaderId },
        }),
        invalidate: [['adminGetGameSessionDetails']],
    });
    adminGetGameSessionRecentNotifications = this.sdkQuery('adminGetGameSessionRecentNotifications', this.apiRpc.admin.gameSessions[':sessionId'].recentNotifications.$get, {
        transformInput: (input) => ({
            param: { sessionId: input.sessionId },
        }),
        enabled: (input) => !!input.sessionId,
    });
}
//# sourceMappingURL=PublicSdk.js.map